import { zodResolver } from "@hookform/resolvers/zod";
import {
  type FormField as ElicitFormField,
  type ElicitRequestedSchema,
  requestedSchemaToDefaultValues,
  requestedSchemaToFormFields,
  requestedSchemaToZod,
} from "@qw/qw-common";
import {
  Button,
  FieldCheckbox,
  FieldCheckboxGroup,
  FieldCombobox,
  FieldDatePicker,
  FieldInput,
  FieldNumber,
  FieldRadioGroup,
  FieldSelect,
  FieldTextarea,
  Icon,
} from "@qw/ui";
import { type FC, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { getElicitConfirmationConfig } from "./elicitation-display";

export interface ElicitFormRendererProps {
  toolCallId: string;
  message: string;
  requestedSchema: ElicitRequestedSchema;
  onSubmit: (toolCallId: string, content: Record<string, unknown>) => Promise<void>;
  onDecline: (toolCallId: string) => Promise<void>;
  onCancel: (toolCallId: string) => Promise<void>;
  disabled?: boolean;
  error?: string | null;
}

export const ElicitFormRenderer: FC<ElicitFormRendererProps> = ({
  toolCallId,
  message,
  requestedSchema,
  onSubmit,
  onCancel,
  disabled = false,
  error: externalError = null,
}) => {
  const [activeAction, setActiveAction] = useState<"submit" | "cancel" | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const displayError = submitError || externalError;

  const handleAction = useCallback(async (type: "submit" | "cancel", action: () => Promise<void>) => {
    setActiveAction(type);
    setSubmitError(null);
    try {
      await action();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit");
    } finally {
      setActiveAction(null);
    }
  }, []);

  const zodSchema = useMemo(() => requestedSchemaToZod(requestedSchema), [requestedSchema]);
  const defaultValues = useMemo(() => requestedSchemaToDefaultValues(requestedSchema), [requestedSchema]);
  const formFields = useMemo(() => requestedSchemaToFormFields(requestedSchema), [requestedSchema]);
  const confirmationConfig = useMemo(() => getElicitConfirmationConfig(requestedSchema), [requestedSchema]);
  const supplementalFields = useMemo(
    () => formFields.filter((field) => confirmationConfig?.supplementalFieldKeys.includes(field.key)),
    [confirmationConfig?.supplementalFieldKeys, formFields]
  );

  const formMethods = useForm({
    resolver: zodResolver(zodSchema as any),
    defaultValues,
    mode: "onTouched",
  });

  const isDisabled = disabled || activeAction !== null;

  const handleAccept = useCallback(
    (values: Record<string, unknown>) => handleAction("submit", () => onSubmit(toolCallId, values)),
    [handleAction, onSubmit, toolCallId]
  );

  const handleConfirmationSubmit = useCallback(
    (confirm: boolean) =>
      handleAction("submit", async () => {
        if (supplementalFields.length > 0) {
          const isValid = await formMethods.trigger(supplementalFields.map((field) => field.key));

          if (!isValid) {
            return;
          }
        }

        const values = formMethods.getValues();
        await onSubmit(toolCallId, { ...values, confirm });
      }),
    [formMethods, handleAction, onSubmit, supplementalFields, toolCallId]
  );

  if (confirmationConfig) {
    const heading = confirmationConfig.kind === "with-details" ? "Review Requested" : "Confirmation";

    return (
      <div className="border border-input rounded-lg bg-popover mt-2 overflow-hidden -mx-2 2xl:-mx-1">
        <div className="flex items-center gap-1.5 border-b border-input py-2 px-3 text-xs text-muted-foreground">
          <Icon icon="tooltip_2" className="text-muted-foreground" />
          <span className="font-medium">{heading}</span>
        </div>
        <div className="p-3 space-y-3">
          <p className="text-sm text-foreground">{message}</p>
          {supplementalFields.length > 0 && (
            <FormProvider {...formMethods}>
              <div className="space-y-3">
                {supplementalFields.map((field) => (
                  <ElicitField key={field.key} field={field} disabled={isDisabled} />
                ))}
              </div>
            </FormProvider>
          )}
          {displayError && <p className="text-xs text-destructive">{displayError}</p>}
          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={isDisabled} onClick={() => handleConfirmationSubmit(true)}>
              {activeAction === "submit" ? "Submitting..." : "Confirm"}
            </Button>
            <Button size="sm" variant="outline" disabled={isDisabled} onClick={() => handleConfirmationSubmit(false)}>
              {activeAction === "submit" ? "Submitting..." : "Deny"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={isDisabled}
              onClick={() => handleAction("cancel", () => onCancel(toolCallId))}
            >
              {activeAction === "cancel" ? "Skipping..." : "Skip"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-input rounded-lg bg-popover mt-2 overflow-hidden -mx-2 2xl:-mx-1">
      <div className="flex items-center gap-1.5 border-b border-input py-2 px-3 text-xs text-muted-foreground">
        <Icon icon="tooltip_2" className="text-muted-foreground" />
        <span className="font-medium">Information Requested</span>
      </div>

      <div className="p-3 space-y-3">
        <p className="text-sm text-foreground">{message}</p>

        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(handleAccept)} className="space-y-3">
            {formFields.map((field) => (
              <ElicitField key={field.key} field={field} disabled={isDisabled} />
            ))}

            {displayError && <p className="text-xs text-destructive">{displayError}</p>}

            <div className="flex gap-2 pt-1">
              <Button type="submit" size="sm" disabled={isDisabled}>
                {activeAction === "submit" ? "Submitting..." : "Submit"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isDisabled}
                onClick={() => handleAction("cancel", () => onCancel(toolCallId))}
              >
                {activeAction === "cancel" ? "Skipping..." : "Skip"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

const ElicitField: FC<{ field: ElicitFormField; disabled: boolean }> = ({ field, disabled }) => {
  switch (field.type) {
    case "text":
    case "email":
    case "uri":
      if (field.constraints?.maxLength && field.constraints.maxLength > 200) {
        return (
          <FieldTextarea
            name={field.key}
            label={field.title}
            description={field.description}
            disabled={disabled}
            maxLength={field.constraints.maxLength}
          />
        );
      }
      return (
        <FieldInput
          name={field.key}
          label={field.title}
          description={field.description}
          disabled={disabled}
          maxLength={field.constraints?.maxLength}
        />
      );

    case "date":
    case "datetime":
      return (
        <FieldDatePicker name={field.key} label={field.title} description={field.description} disabled={disabled} />
      );

    case "number":
      return (
        <FieldNumber
          name={field.key}
          label={field.title}
          description={field.description}
          disabled={disabled}
          {...(!field.isInteger && { inputProps: { decimalScale: 10 } })}
        />
      );

    case "boolean":
      return <FieldCheckbox name={field.key} label={field.title} description={field.description} disabled={disabled} />;

    case "enum":
      if (field.options && field.options.length <= 4) {
        return (
          <FieldRadioGroup
            name={field.key}
            label={field.title}
            description={field.description}
            options={field.options.map((o) => ({ value: o.value, label: o.label }))}
            disabled={disabled}
          />
        );
      }
      return (
        <FieldSelect
          name={field.key}
          label={field.title}
          description={field.description}
          options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          disabled={disabled}
        />
      );

    case "multi-select":
      if (field.options && field.options.length > 4) {
        return (
          <FieldCombobox
            name={field.key}
            label={field.title}
            description={field.description}
            multiple
            options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
            disabled={disabled}
          />
        );
      }
      return (
        <FieldCheckboxGroup
          name={field.key}
          label={field.title}
          description={field.description}
          options={(field.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          disabled={disabled}
        />
      );

    default:
      return <FieldInput name={field.key} label={field.title} description={field.description} disabled={disabled} />;
  }
};
